import { useCallback, useMemo, useState } from "react";
import { addMonths, format, isSameDay, isSameMonth, subMonths } from "date-fns";

import { AppCTAModal, Button, PageLoader } from "@/components";
import { Header } from "@/app/modules/Navigation";
import { cn } from "@/utils";
import { useResponsiveness, useTranslate } from "@/providers/context";
import { useAppCTAModal } from "@/hooks";
import { DRAWER_CALENDAR, DRAWER_SCHEDULE } from "@/const.ts";
import { Video } from "@/api";

import { useCalendarData } from "./hooks/useCalendarData.tsx";
import { getCalendarDates, getWeekHeaders } from "./util.tsx";
import { MonthSelector } from "./components/MonthSelector.tsx";
import { CalendarDrawer } from "./components/CalendarDrawer.tsx";
import { CalendarDayDrawer } from "./components/CalendarDayDrawer.tsx";

const getRounded = (idx: number): string => {
  switch (idx) {
    case 0:
      return "rounded-tl-lg";
    case 6:
      return "rounded-tr-lg";
    case 35:
      return "rounded-bl-lg";
    case 41:
      return "rounded-br-lg";
    default:
      return "";
  }
};
const getMonthFromDate = (date: Date) => {
  return format(date, "yyyy-MM");
};
const getDateFromMonth = (dateString: string) => {
  const [year, month] = dateString.split("-").map(Number);
  // Note: month is 0-indexed in JavaScript Date, so subtract 1
  return new Date(year, month - 1);
};

export const CalendarScreen = () => {
  const currentDate = new Date();
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [visibleMonth, setVisibleMonth] = useState(
    getMonthFromDate(currentDate),
  );

  const visibleMonthDate = getDateFromMonth(visibleMonth);

  const {
    getEventsForDate,
    getJournalEntriesForDate,
    getEventTitle,
    getEventIcon,
    getEventTime,
    isLoading,
  } = useCalendarData();
  const { openDrawer, setOpenDrawer, drawerMargin, transitionStyle } =
    useResponsiveness();
  const { translate } = useTranslate();
  const [scheduleVideo, setScheduleVideo] = useState<Video | undefined>();
  const { modalOpen, modalItem, setModalOpen } = useAppCTAModal();

  const calendarDates = useMemo(() => {
    return getCalendarDates(visibleMonthDate);
  }, [visibleMonthDate]);

  const handleClick = useCallback(
    (d: Date) => {
      setSelectedDate(d);
      setOpenDrawer(DRAWER_CALENDAR);
    },
    [setOpenDrawer],
  );

  const setToday = () => {
    setSelectedDate(currentDate);
    setVisibleMonth(getMonthFromDate(currentDate));
  };

  const isDrawerOpen =
    openDrawer === DRAWER_CALENDAR || openDrawer === DRAWER_SCHEDULE;

  const headers = getWeekHeaders();
  const weekHeaders = useMemo(() => {
    return headers.map((header) => (
      <div key={header} className="caption-bold pb-2 text-item-disabled">
        {header}
      </div>
    ));
  }, [headers]);

  return (
    <div
      style={{ marginRight: drawerMargin, transition: transitionStyle }}
      className="h-full"
    >
      <Header
        className="px-7"
        content={<div className="h5-bold">{translate("Calendar")}</div>}
      />
      {isLoading ? (
        <PageLoader />
      ) : (
        <>
          <div className="mt-0.5 flex justify-between px-7">
            <MonthSelector
              month={visibleMonthDate}
              setMonth={(month) => setVisibleMonth(getMonthFromDate(month))}
              isMaxMonth={(month) =>
                isSameMonth(month, addMonths(currentDate, 3))
              }
              isMinMonth={(month) =>
                isSameMonth(month, subMonths(currentDate, 6))
              }
            />
            <Button
              variant="secondary"
              size="s"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setToday();
              }}
            >
              {translate("Today")}
            </Button>
          </div>
          <div className="my-2 mt-5 grid grid-cols-7 px-7 pb-7">
            {weekHeaders}
            {calendarDates?.map((d, idx) => {
              const isSelected = isSameDay(selectedDate, d);
              const isToday = isSameDay(currentDate, d);
              const date = format(d, "yyyy-MM-dd");
              const eventsForDate = getEventsForDate(d);
              const hasJournalEntry = getJournalEntriesForDate(d).length > 0;

              return (
                <div
                  data-testid={`calendar-${date}`}
                  key={date}
                  onClick={() => handleClick(d)}
                  className={cn(
                    "h-[140px] border-collapse cursor-pointer border border-border p-2 text-item-primary hover:border-border-hover hover:bg-surface-secondary",
                    getRounded(idx),
                    !isSameMonth(d, visibleMonthDate) &&
                      "bg-surface-secondary font-normal text-item-disabled",
                    isSelected &&
                      "border-highlightPrimary bg-highlightBg text-item-primary hover:border-highlightPrimary hover:bg-highlightBg",
                    isDrawerOpen
                      ? "lg:flex lg:flex-col lg:items-center xl:block"
                      : "",
                  )}
                >
                  <div className="flex w-full items-center justify-between">
                    <div
                      className={cn(
                        "flex h-[28px] w-[28px] items-center justify-center rounded-full",
                        isSelected && "text-highlightPrimary",
                        isToday || isSelected ? "p2-bold" : "p2-medium",
                        isToday && !isSelected && "bg-surface-secondary",
                      )}
                    >
                      {/* The day of the month */}
                      {format(d, "dd")}
                    </div>
                    {hasJournalEntry && (
                      <div className="h-[6px] w-[6px] rounded-full bg-journalColor" />
                    )}
                  </div>
                  <div className={cn("mt-2 flex flex-col px-1")}>
                    {eventsForDate
                      ?.filter((e) => !e.isClientScheduled)
                      .slice(0, 3)
                      ?.map((e) => {
                        // Render each user event in this cell
                        return (
                          <div
                            data-testid={`${e.EventType}-event`}
                            className={cn(
                              "mb-2 flex flex-row items-center justify-start",
                              isDrawerOpen
                                ? "lg:flex-col xl:flex-row"
                                : "lg:justify-between",
                              isDrawerOpen &&
                                eventsForDate.length > 2 &&
                                "lg:last:hidden xl:last:flex",
                            )}
                            key={e.EventDate + e.EventType + e.ItemId}
                          >
                            <div
                              className={cn(
                                "mr-1",
                                isDrawerOpen ? "lg:mr-0 xl:mr-1" : "",
                              )}
                            >
                              {getEventIcon(e)}
                            </div>
                            <div
                              className={cn(
                                "p3-medium mr-1 w-full overflow-hidden overflow-ellipsis text-nowrap",
                                isDrawerOpen
                                  ? "hidden xl:block"
                                  : "hidden lg:block",
                              )}
                            >
                              {getEventTitle(e)}
                            </div>
                            <div className="subtitle text-item-secondary">
                              {getEventTime(e)}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                  {eventsForDate?.length > 3 && (
                    <div className="subtitle px-1 text-item-secondary">
                      {translate("See all")}
                    </div>
                  )}
                </div>
              );
            })}
            <CalendarDayDrawer
              selectedDate={selectedDate}
              setScheduleVideo={setScheduleVideo}
            />
            {scheduleVideo && (
              <CalendarDrawer
                title={translate("Schedule again")}
                open={openDrawer === DRAWER_SCHEDULE}
                setOpen={(open) => setOpenDrawer(open ? DRAWER_SCHEDULE : "")}
                video={scheduleVideo}
              />
            )}
          </div>
        </>
      )}
      <AppCTAModal open={modalOpen} setOpen={setModalOpen} item={modalItem} />
    </div>
  );
};
